import {Autocomplete, AutocompleteItem, SelectItem} from "@nextui-org/react";
import {useEffect, useMemo, useState} from "react";
import {FaCalendarAlt, FaLayerGroup} from "react-icons/fa";
import {MdDirectionsBus} from "react-icons/md";
import {NeoDatePicker, NeoSelect} from "../forms";

const mainFilter = [
  {label: "Today", value: "1"},
  {label: "Yesterday", value: "2"},
  {label: "Current Week", value: "3"},
  {label: "Last Week", value: "4"},
  {label: "Current Month", value: "5"},
  {label: "Last Month", value: "6"},
  {label: "Range", value: "7"}
];
type InlineFilterProps = {
  filtertype: 1 | 2 | 3;
  onFilterChange: (request: {from: number; to: number}) => void;
  vehicles?: Vehicle[];
  defaultVehicleKey?: string;
  onVehicleChange?: (trackerId: string | null) => void;
  handleGroupChange?: (value: any) => void;
};
export const InlineFilter: React.FC<any> = ({
  filtertype = 1,
  onFilterChange,
  vehicles = [],
  defaultVehicleKey,
  onVehicleChange,
  handleGroupChange
}: InlineFilterProps) => {
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(
    null
  );
  const [showDateRange, setShowDateRange] = useState(false);
  const [selectedFilterType, setSelectedFilterType] = useState("1");
  const groups = [
    {label: "Vehicle", value: "1"},
    {label: "Date", value: "2"}
  ];
  useEffect(() => {
    handleFromToFilterData("5"); // set default to 'Today' on mount
  }, []);

  //   When selectedDateRange changes, call onFilterChange
  useEffect(() => {
    if (filtertype === 1 && selectedDateRange?.from && selectedDateRange?.to) {
      const fromTimestamp = new Date(selectedDateRange?.from);
      fromTimestamp.setHours(0, 0, 0, 0);
      const toTimestamp = new Date(selectedDateRange?.to);
      toTimestamp.setHours(23, 59, 59, 999);
      onFilterChange({
        from: fromTimestamp.getTime(),
        to: toTimestamp.getTime()
      });
    }
  }, [selectedDateRange]);

  const handleFromToFilterData = (filterType: string) => {
    const currentDate = new Date();
    if (filterType === "1") {
      const from = new Date(currentDate);
      const to = new Date(currentDate);
      from.setHours(0, 0, 0, 0);
      to.setHours(23, 59, 59, 999);
      updateRange({from, to});
    } else if (filterType === "2") {
      const from = new Date(currentDate);
      from.setDate(currentDate.getDate() - 1);
      const to = new Date(currentDate);
      to.setDate(currentDate.getDate() - 1);
      from.setHours(0, 0, 0, 0);
      to.setHours(23, 59, 59, 999);
      updateRange({from, to});
    } else if (filterType === "3" || filterType === "4") {
      const dayOfWeek = currentDate.getDay();
      const diffToMonday = (dayOfWeek + 6) % 7;
      const weekStart = new Date(currentDate);
      weekStart.setDate(currentDate.getDate() - diffToMonday);
      weekStart.setHours(0, 0, 0, 0);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      if (filterType === "3") {
        const to = new Date(currentDate);
        to.setHours(23, 59, 59, 999);
        updateRange({from: weekStart, to});
      } else {
        const lastWeekFrom = new Date(weekStart);
        lastWeekFrom.setDate(weekStart.getDate() - 7);
        const lastWeekTo = new Date(weekEnd);
        lastWeekTo.setDate(weekEnd.getDate() - 7);
        updateRange({from: lastWeekFrom, to: lastWeekTo});
      }
    } else if (filterType === "5") {
      const from = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );
      const to = new Date(currentDate);
      to.setHours(23, 59, 59, 999);
      updateRange({from, to});
    } else if (filterType === "6") {
      const from = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 1,
        1
      );
      const to = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
      to.setHours(23, 59, 59, 999);
      updateRange({from, to});
    } else if (filterType === "7") {
      setShowDateRange(true);
      return;
    }
    setSelectedFilterType(filterType);
    setShowDateRange(false);
  };

  const updateRange = (range: DateRange) => {
    setSelectedDateRange(range);
  };

  const handleDateChange = (range: DateRange | Date | Date[] | null): void => {
    if (
      range &&
      typeof range === "object" &&
      "from" in range &&
      "to" in range
    ) {
      setSelectedDateRange(range as DateRange);
    }
  };

  const handleFilterTypeChange = (value: string) => {
    setSelectedFilterType(value);
    handleFromToFilterData(value);
  };

  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter(
      (v: any) =>
        (v.trackerName || "").toLowerCase().includes(q) ||
        (v.trackerId || "").toLowerCase().includes(q)
    );
  }, [vehicles, query]);

  return (
    <>
      <div className="flex items-center gap-2">
        {filtertype === 1 && (
          <>
            <div className="h-10 w-40">
              <NeoSelect
                selectedKeys={[selectedFilterType]}
                startContent={<FaCalendarAlt className="text-[#1e2a44]" />}
                onSelectionChange={keys =>
                  handleFilterTypeChange(Array.from(keys)[0] as string)
                }
                size="md"
                placeholder="Select Date Type"
              >
                {mainFilter.map(item => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </NeoSelect>
            </div>
            {showDateRange && (
              <div className="">
                <NeoDatePicker
                  type="range"
                  handleDateChange={handleDateChange}
                  rangedefaultValue={selectedDateRange}
                />
              </div>
            )}
          </>
        )}
        {filtertype === 2 && (
          <Autocomplete
            aria-label="vehicle"
            placeholder="Select vehicle"
            startContent={
              <MdDirectionsBus className="h-6 w-6 text-[#1e2a44]" />
            }
            defaultSelectedKey={defaultVehicleKey}
            onSelectionChange={key =>
              onVehicleChange?.(key ? String(key) : null)
            }
          >
            {vehicles.map((v: any) => (
              <AutocompleteItem
                key={v.trackerId}
                textValue={`${v.trackerName}`}
                className="whitespace-normal break-words"
              >
                {v.trackerName}
              </AutocompleteItem>
            ))}
          </Autocomplete>
        )}
        {filtertype === 3 && (
          <div className="h-10 w-40">
            <NeoSelect
              label=""
              size="md"
              className="w-full"
              placeholder="Group by"
              onSelectionChange={keys => {
                const first = Array.isArray(keys)
                  ? keys[0]
                  : Array.from(keys as Set<unknown>)[0];
                const value: any =
                  first !== undefined && first !== null ? String(first) : null;
                handleGroupChange?.(value);
              }}
              startContent={<FaLayerGroup className="h-4 w-4 text-[#1e2a44]" />}
            >
              {groups.map(item => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </NeoSelect>
          </div>
        )}
      </div>
    </>
  );
};
